/* eslint-disable max-lines */
import StepShell from '../StepShell';
import { colors, layout } from '../styles';
import usePreEmploymentDraft from './preEmployment/usePreEmploymentDraft';
import useSaveLaterHandler from './preEmployment/useSaveLaterHandler';
import { PreEmploymentFormData } from './preEmployment/PreEmploymentTypes';
import { BaseStepProps } from './BaseStepProps';

const educationGroup = (
  prefix: 'highSchool' | 'collegeUniversity' | 'graduateProfessional',
  label: string,
  formData: PreEmploymentFormData,
  updateForm: (patch: Partial<PreEmploymentFormData>) => void,
  showSecond: boolean,
  requiredFirstEntry?: boolean,
) => {
  const k = (suffix: string, second?: boolean): keyof PreEmploymentFormData =>
    `${prefix}${suffix}${second ? '2' : ''}` as keyof PreEmploymentFormData;
  const renderEntry = (second: boolean) => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 8 }}>
      <div>
        <label style={layout.label}>
          Name & Location {second ? '(2)' : ''} {requiredFirstEntry && !second ? '*' : ''}
        </label>
        <input
          type="text"
          value={String(formData[k('NameLocation', second)] || '')}
          onChange={(e) =>
            updateForm({ [k('NameLocation', second)]: e.target.value } as Partial<PreEmploymentFormData>)
          }
          style={layout.input}
        />
      </div>
      <div>
        <label style={layout.label}>
          Dates Attended {second ? '(2)' : ''} {requiredFirstEntry && !second ? '*' : ''}
        </label>
        <input
          type="text"
          value={String(formData[k('DatesAttended', second)] || '')}
          onChange={(e) =>
            updateForm({ [k('DatesAttended', second)]: e.target.value } as Partial<PreEmploymentFormData>)
          }
          placeholder="e.g., 2018-2022"
          style={layout.input}
        />
      </div>
      <div>
        <label style={layout.label}>Course of Study {second ? '(2)' : ''}</label>
        <input
          type="text"
          value={String(formData[k('CourseOfStudy', second)] || '')}
          onChange={(e) =>
            updateForm({ [k('CourseOfStudy', second)]: e.target.value } as Partial<PreEmploymentFormData>)
          }
          style={layout.input}
        />
      </div>
      <div>
        <label style={layout.label}>Degree / Diploma {second ? '(2)' : ''}</label>
        <input
          type="text"
          value={String(formData[k('DegreeDiploma', second)] || '')}
          onChange={(e) =>
            updateForm({ [k('DegreeDiploma', second)]: e.target.value } as Partial<PreEmploymentFormData>)
          }
          style={layout.input}
        />
      </div>
    </div>
  );
  return (
    <div style={{ marginBottom: 18 }}>
      <h4 style={{ fontSize: 14, fontWeight: 700, margin: '12px 0 8px' }}>{label}</h4>
      {renderEntry(false)}
      {showSecond && renderEntry(true)}
    </div>
  );
};

const PreEmploymentEducationStep = ({
  step,
  stepIndex,
  totalSteps,
  completedSteps,
  token,
  tokenData,
  applicationDraft,
  onBack,
  onPrevious,
  onNext,
  onSaveLater,
}: BaseStepProps) => {
  const { formData, updateForm, saveAsDraft } = usePreEmploymentDraft(token, applicationDraft);
  const { handleSaveLater } = useSaveLaterHandler({
    saveAsDraft: () => saveAsDraft({ token, userId: tokenData.userId, userName: tokenData.userName }),
    onSaveLater,
  });

  const canSubmit = !!formData.highSchoolNameLocation && !!formData.highSchoolDatesAttended;

  return (
    <StepShell
      stepNumber={stepIndex + 1}
      totalSteps={totalSteps}
      completedSteps={completedSteps}
      title={step.title}
      onBack={onBack}
      onPrevious={onPrevious}
      onSaveLater={handleSaveLater}
      onNext={onNext}
      nextLabel="Next"
      nextDisabled={!canSubmit}
    >
      <h3 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Education History</h3>
      <p style={{ color: colors.muted, fontSize: 14, marginTop: 6 }}>
        List your educational background and any relevant credentials. Leave blank what doesn&apos;t apply.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div>
          <label style={layout.label}>Highest grade completed</label>
          <input
            type="text"
            value={formData.highestGradeCompleted}
            onChange={(e) => updateForm({ highestGradeCompleted: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>GED / College completed?</label>
          <input
            type="text"
            value={formData.gedCollegeCompleted}
            onChange={(e) => updateForm({ gedCollegeCompleted: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>

      {educationGroup('highSchool', 'High School *', formData, updateForm, true, true)}
      {educationGroup('collegeUniversity', 'College / University', formData, updateForm, true)}
      {educationGroup('graduateProfessional', 'Graduate / Professional', formData, updateForm, true)}

      <h4 style={{ fontSize: 14, fontWeight: 700, margin: '12px 0 8px' }}>State Credentials / Certifications</h4>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={layout.label}>Name & Location</label>
          <input
            type="text"
            value={formData.stateCredentialNameLocation}
            onChange={(e) => updateForm({ stateCredentialNameLocation: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Dates Attended</label>
          <input
            type="text"
            value={formData.stateCredentialDatesAttended}
            onChange={(e) => updateForm({ stateCredentialDatesAttended: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Course of Study</label>
          <input
            type="text"
            value={formData.stateCredentialCourseOfStudy}
            onChange={(e) => updateForm({ stateCredentialCourseOfStudy: e.target.value })}
            style={layout.input}
          />
        </div>
        <div>
          <label style={layout.label}>Degree / Diploma</label>
          <input
            type="text"
            value={formData.stateCredentialDegreeDiploma}
            onChange={(e) => updateForm({ stateCredentialDegreeDiploma: e.target.value })}
            style={layout.input}
          />
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={layout.label}>Childcare Training (specific certifications, hours, etc.)</label>
        <textarea
          rows={3}
          value={formData.childcareTraining}
          onChange={(e) => updateForm({ childcareTraining: e.target.value })}
          style={{ ...layout.input, resize: 'vertical' }}
        />
      </div>
    </StepShell>
  );
};

export default PreEmploymentEducationStep;
